import { computed, ref, watch } from 'vue'
import { useFetch } from '@/composables'
import type { FetchDataParams, RequestParams } from '@/types'

export interface PageDataResponse<T = unknown> {
  list?: T[]
  filter?: unknown
  data?: T[]
  total?: number
}

export const useFetchPageListData = <Item = unknown, P extends RequestParams = RequestParams>({
  url,
  transform,
  initialData,
  page = 1,
  pageSize = 10,
  params = {} as P,
  method = 'POST',
  debounceTime = 0,
  errorMessage = '请求出错',
  showErrorMessage,
  autoFetch = true,
  onSuccess,
  onComplete,
  onFailure,
}: FetchDataParams<PageDataResponse<Item>, PageDataResponse<Item>, P> & {
  page?: number
  pageSize?: number
}) => {
  const effectiveInitialData = (initialData ?? {
    total: 0,
    list: [],
    data: [],
    filter: {},
  }) as PageDataResponse<Item>

  const _page = ref(page)
  const _pageSize = ref(pageSize)
  const dataSource = ref<Item[]>([])
  const isAppend = ref(false)

  const onChange = (page: number, pageSize: number) => {
    _page.value = page
    _pageSize.value = pageSize
  }

  const finalParams = ref({
    ...params,
    page: _page.value,
    page_size: _pageSize.value,
  })

  const {
    loading,
    data,
    fetchData: _fetchData,
    reset,
  } = useFetch<PageDataResponse<Item>, PageDataResponse<Item>, P>({
    url,
    transform,
    initialData: effectiveInitialData,
    params: finalParams as P,
    method,
    debounceTime,
    errorMessage,
    showErrorMessage,
    autoFetch,
    onSuccess,
    onComplete,
    onFailure,
  })

  // 监听分页参数变化，自动触发数据请求
  watch([_page, _pageSize], ([newPage, newPageSize], [oldPage, oldPageSize]) => {
    finalParams.value = {
      ...params,
      page: newPage,
      page_size: newPageSize,
    }
    // 当页码或每页数量变化时，自动触发数据请求（排除初始化时的触发）
    if (oldPage !== undefined || oldPageSize !== undefined) {
      _fetchData(finalParams.value as P)
    }
  })

  const loadMore = async (params: P, append?: boolean) => {
    isAppend.value = append ?? false
    await _fetchData(params)
  }

  watch(data, (newData) => {
    const responseData = newData?.list ?? newData?.data ?? []
    if (isAppend.value) {
      dataSource.value = [...dataSource.value, ...responseData] as Item[]
      isAppend.value = false
    } else {
      dataSource.value = responseData as Item[]
    }
  })

  const total = computed(() => {
    return (data.value as PageDataResponse<Item>)?.total
  })

  const pagination = computed(() => ({
    current: _page.value,
    pageSize: _pageSize.value,
    total: total.value,
    onChange,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number) => `共 ${total} 条`,
  }))

  return {
    loading,
    page: _page,
    pageSize: _pageSize,
    total,
    pagination,
    data,
    dataSource,
    loadMore,
    fetchData: _fetchData,
    reset,
  }
}
